"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

import img1 from "../../assets/images/expedition-gear/backpacking/backpack12.png";
import img2 from "../../assets/images/expedition-gear/backpacking/backpack11.png";
import img3 from "../../assets/images/expedition-gear/backpacking/backpack14.png";
import img4 from "../../assets/images/expedition-gear/backpacking/backpack13.png";

/* ================= PACKING ZONES ================= */

const PACKING_ZONES = [
  {
    zone: "Bottom Layer",
    role: "Stability",
    text: "Bulky, lightweight items such as \nsleeping bag and camp clothes.",
    image: img1,
  },
  {
    zone: "Core Zone",
    role: "Balance",
    text: `Heaviest gear packed close to the spine\n including food, water, and stove.`,
    image: img2,
  },
  {
    zone: "Top Layer",
    role: "Speed",
    text: "Rapid-access items like rain shell,\n snacks, and first aid.",
    image: img3,
  },
  {
    zone: "External Pockets",
    role: "Immediate Access",
    text: "Map, headlamp, knife, and \nsunscreen.",
    image: img4,
  },
];

/* ================= CARD ================= */

function StickyCard({ card, index, progress, total }) {
  const start = index / total;
  const end = (index + 1) / total;

  const y = useTransform(progress, [start, end], [120, 0]);
  const scale = useTransform(progress, [start, end], [0.9, 1]);
  const opacity = useTransform(progress, [start, end], [0, 1]);

  return (
    <motion.div
      style={{ y, scale, opacity, zIndex: index + 1 }}
      className="absolute inset-0 flex items-center justify-center"
    >
      <div className="relative w-full h-full rounded-e-full overflow-hidden bg-neutral-900 shadow-2xl">
        {/* IMAGE */}
        <img
          src={card.image}
          alt={card.zone}
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* OVERLAY */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#101d29]/85 via-transparent to-transparent" />

        {/* INFO */}
        <div className="absolute bottom-0 p-6 sm:p-8 lg:p-10 max-w-xl">
          <span className="inline-block mb-3 text-[10px] sm:text-xs uppercase tracking-widest bg-white/10 text-white px-3 py-1 rounded-full">
            {card.role}
          </span>

          <h3 className="text-xl sm:text-2xl font-semibold text-white mb-2 sm:mb-3">
            {card.zone}
          </h3>

          <p className="text-xs sm:text-sm text-neutral-300 whitespace-pre-line leading-relaxed">
            {card.text}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

/* ================= SECTION ================= */

export default function PackingInformation() {
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  return (
    <section
      ref={sectionRef}
      className="relative h-[360vh] sm:h-[380vh] lg:h-[400vh] bg-[#101d29]"
    >
      {/* STICKY WRAPPER */}
      <div className="sticky top-0 h-screen flex items-center overflow-hidden">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* RIGHT: HEADER / CONTENT */}
          <div className="relative z-10 order-1 lg:order-2">
            <motion.div
              className="inline-block px-4 py-1 mb-4 sm:mb-6 border bg-slate-300/30 border-black/20 rounded-full text-[10px] sm:text-xs uppercase tracking-widest text-white"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Load Distribution Strategy
            </motion.div>

            <h2 className="mt-4 sm:mt-6 text-2xl sm:text-3xl md:text-4xl font-semibold text-white max-w-xl leading-snug sm:leading-tight">
              Packing zones designed to
              <br />
              maintain balance and efficiency
            </h2>
          </div>

          {/* LEFT: CARD STACK */}
          <div className="relative w-full h-[55vh] sm:h-[60vh] lg:h-[70vh] order-2 lg:order-1">
            {PACKING_ZONES.map((zone, i) => (
              <StickyCard
                key={i}
                card={zone}
                index={i}
                total={PACKING_ZONES.length}
                progress={scrollYProgress}
              />
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
